import { cva } from "class-variance-authority"

// =============================================================================
// Styles shared by `TabLinks` and `Tabs`
// =============================================================================

export type TabsBaseOrientation = "horizontal" | "vertical"

export const tabsBaseContainerStyles = cva("", {
  variants: {
    orientation: {
      horizontal: [
        "relative",
        "min-w-0",
        // The gutter fits the item focus indicator's offset and width, so the
        // scroll container doesn't clip it
        "[--tabs-focus-indicator-offset:0.5rem]",
        "[--tabs-gutter:calc(var(--tabs-focus-indicator-offset)+0.125rem)]",
        "mx-[calc(var(--tabs-gutter)*-1)]"
      ],
      vertical: ""
    }
  }
})

export const tabsBaseListStyles = cva("", {
  variants: {
    orientation: {
      horizontal: [
        "flex gap-x-6",
        "overflow-x-auto overscroll-x-contain",
        "px-(--tabs-gutter)",
        "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        // The fade is fully transparent under the scroll button, so the button
        // reads on any background, and wider than the gap between items, so it
        // always covers some of a hidden item
        "[--tabs-scroll-button-size:1.5rem] [--tabs-fade-size:3.5rem]",
        "[--tabs-fade-left:black] [--tabs-fade-right:black]",
        // Prevent focus indicators from being clipped by fades on scroll
        "scroll-px-[calc(var(--tabs-fade-size)+var(--tabs-gutter))]",
        "data-[overflow-left]:[--tabs-fade-left:transparent] data-[overflow-right]:[--tabs-fade-right:transparent]",
        "[mask-image:linear-gradient(to_right,var(--tabs-fade-left),var(--tabs-fade-left)_var(--tabs-scroll-button-size),black_var(--tabs-fade-size),black_calc(100%_-_var(--tabs-fade-size)),var(--tabs-fade-right)_calc(100%_-_var(--tabs-scroll-button-size)),var(--tabs-fade-right))]"
      ],
      vertical: "flex flex-col gap-y-2"
    }
  }
})

// An item is selected by `aria-current` on a `TabLinks.Item` and by Base UI's
// `data-active` on a `Tabs.Tab`
export const tabsBaseItemStyles = cva("", {
  variants: {
    orientation: {
      horizontal: [
        "relative",
        "border-b border-transparent aria-[current]:border-gray-900 data-[active]:border-gray-900",
        "[--tabs-item-padding-y:1rem] py-(--tabs-item-padding-y)",
        "inline-flex items-center gap-x-2",
        "text-sm leading-6 font-semibold text-gray-700 text-nowrap aria-[current]:text-gray-900 data-[active]:text-gray-900",
        "transition-colors duration-200",
        "not-aria-disabled:hover:border-gray-700 aria-[current]:hover:border-gray-900 data-[active]:hover:border-gray-900",
        "aria-disabled:text-gray-600 aria-disabled:cursor-not-allowed",
        // Add an inset focus indicator
        "focus-visible:outline-none",
        "before:absolute before:inset-x-[calc(var(--tabs-focus-indicator-offset)*-1)] before:inset-y-(--tabs-item-padding-y) before:rounded-sm",
        "before:outline-2 before:outline-transparent before:transition-colors before:duration-200",
        "focus-visible:before:outline-gray-900"
      ],
      vertical: [
        "outline-2 outline-offset-2 outline-transparent",
        "rounded-sm bg-white",
        "py-2 px-3 has-data-[slot=icon]:pl-2 has-data-[slot=count-badge]:pr-2",
        "flex items-center gap-x-2",
        "text-sm leading-6 font-semibold text-gray-700",
        "transition-colors",
        "not-aria-disabled:hover:bg-gray-900/6 focus-visible:outline-gray-900",
        "aria-disabled:text-gray-600 aria-disabled:cursor-not-allowed",
        "aria-[current]:bg-gray-900/6 aria-[current]:text-gray-900 data-active:bg-gray-900/6 data-active:text-gray-900",
        "[&_[data-slot=count-badge]]:ml-auto"
      ]
    }
  }
})
